// src/components/ui/Alert.tsx
import React, { useState } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info,
  X,
} from 'lucide-react';

type AlertVariant = 'info' | 'success' | 'warning' | 'danger';

interface AlertProps {
  variant?: AlertVariant;
  title?: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
  border?: boolean;
  action?: React.ReactNode;
}

const variantConfig: Record<
  AlertVariant,
  {
    bg: string;
    border: string;
    text: string;
    iconColor: string;
    icon: React.ReactNode;
  }
> = {
  info: {
    bg: 'bg-info/5',
    border: 'border-info/20',
    text: 'text-info',
    iconColor: 'text-info',
    icon: <Info className="w-5 h-5" />,
  },
  success: {
    bg: 'bg-success/5',
    border: 'border-success/20',
    text: 'text-success',
    iconColor: 'text-success',
    icon: <CheckCircle className="w-5 h-5" />,
  },
  warning: {
    bg: 'bg-warning/5',
    border: 'border-warning/20',
    text: 'text-warning',
    iconColor: 'text-warning',
    icon: <AlertTriangle className="w-5 h-5" />,
  },
  danger: {
    bg: 'bg-danger/5',
    border: 'border-danger/20',
    text: 'text-danger',
    iconColor: 'text-danger',
    icon: <XCircle className="w-5 h-5" />,
  },
};

const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  title,
  children,
  icon,
  dismissible = false,
  onDismiss,
  className = '',
  border = true,
  action,
}) => {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;

  const config = variantConfig[variant];

  const handleDismiss = () => {
    setVisible(false);
    onDismiss?.();
  };

  return (
    <div
      role="alert"
      className={`
        relative flex items-start gap-3 p-4 rounded-xl
        ${config.bg}
        ${border ? `border ${config.border}` : ''}
        ${className}
      `}
    >
      {/* Icon */}
      <div className={`shrink-0 mt-0.5 ${config.iconColor}`}>
        {icon || config.icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {title && (
          <h4 className={`text-sm font-bold ${config.text} mb-0.5`}>
            {title}
          </h4>
        )}
        <div className={`text-sm ${config.text} opacity-90 leading-relaxed`}>
          {children}
        </div>
        {action && <div className="mt-3">{action}</div>}
      </div>

      {/* Dismiss Button */}
      {dismissible && (
        <button
          onClick={handleDismiss}
          className={`
            shrink-0 p-1 rounded-lg transition-colors
            ${config.text} opacity-60 hover:opacity-100
            hover:bg-black/5
          `}
          aria-label="Tutup"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// ==================== Alert Inline (compact version) ====================
interface AlertInlineProps {
  variant?: AlertVariant;
  children: React.ReactNode;
  className?: string;
}

export const AlertInline: React.FC<AlertInlineProps> = ({
  variant = 'info',
  children,
  className = '',
}) => {
  const config = variantConfig[variant];

  return (
    <div
      className={`
        flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium
        ${config.bg} ${config.text}
        ${className}
      `}
    >
      <span className={`shrink-0 ${config.iconColor}`}>
        {React.cloneElement(config.icon as React.ReactElement<any>, {
          className: 'w-3.5 h-3.5',
        })}
      </span>
      <span>{children}</span>
    </div>
  );
};

// ==================== Alert Banner (full-width) ====================
interface AlertBannerProps {
  variant?: AlertVariant;
  children: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
  action?: React.ReactNode;
}

export const AlertBanner: React.FC<AlertBannerProps> = ({
  variant = 'info',
  children,
  dismissible = false,
  onDismiss,
  className = '',
  action,
}) => {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;

  const config = variantConfig[variant];

  const handleDismiss = () => {
    setVisible(false);
    onDismiss?.();
  };

  return (
    <div
      role="alert"
      className={`
        flex items-center justify-between gap-4 px-4 py-3 rounded-xl
        ${config.bg} border ${config.border}
        ${className}
      `}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className={`shrink-0 ${config.iconColor}`}>
          {config.icon}
        </span>
        <p className={`text-sm font-medium ${config.text} truncate`}>
          {children}
        </p>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {action}
        {dismissible && (
          <button
            onClick={handleDismiss}
            className={`p-1 rounded-lg ${config.text} opacity-60 hover:opacity-100 hover:bg-black/5 transition-colors`}
            aria-label="Tutup"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Alert;